import React, { useEffect, useState } from 'react';
import axios from '@/lib/axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Loader2,
    MessageSquare,
    Plus,
    Calendar,
    User,
    Tag,
    CheckCircle2,
    Clock,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    StickyNote,
} from 'lucide-react';
import AddNoteDialog, { type ContactOption } from '@/CRM/components/AddNoteDialog';
import { useAuth } from '@/contexts/AuthContext';
import { crmService } from '@/CRM/crmApi';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CRMLeadNote {
    id: number;
    lead: number;
    date: string;
    author: string;
    text: string;
    category: string;
    sentiment: string;
    priority: string | null;
    status: string;
    next_meeting: string | null;
    next_meeting_time: string | null;
    contact_name: string | null;
}

interface LeadContact {
    id: string | number;
    name: string;
    type?: string;
}

export interface CRMLeadNotesModalProps {
    leadId: number | null;
    leadName?: string;
    isOpen: boolean;
    onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDateTime = (iso: string | null): string => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return iso;
    }
};

const formatDate = (d: string | null): string => {
    if (!d) return '-';
    try {
        return new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return d;
    }
};

const sentimentColor: Record<string, string> = {
    positive: 'bg-green-100 text-green-700 border-green-300',
    neutral: 'bg-gray-100 text-gray-600 border-gray-300',
    negative: 'bg-red-100 text-red-600 border-red-300',
};

const priorityColor: Record<string, string> = {
    low: 'bg-blue-50 text-blue-600 border-blue-200',
    medium: 'bg-amber-50 text-amber-600 border-amber-200',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    urgent: 'bg-red-100 text-red-700 border-red-300',
};

const statusIcon: Record<string, React.ReactNode> = {
    completed: <CheckCircle2 className="w-3 h-3 text-green-500" />,
    scheduled: <Clock className="w-3 h-3 text-blue-500" />,
    postponed: <AlertCircle className="w-3 h-3 text-amber-500" />,
};

const categoryIcon: Record<string, string> = {
    call: '📞',
    email: '✉️',
    meeting: '🤝',
    internal: '🏢',
    whatsapp: '💬',
    other: '📝',
};

// ─── NoteCard ────────────────────────────────────────────────────────────────

const NoteCard: React.FC<{ note: CRMLeadNote }> = ({ note }) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = note.text.length > 200;
    const displayText = isLong && !expanded ? note.text.slice(0, 200) + '…' : note.text;

    return (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-4 py-3 bg-gray-50/60 border-b border-gray-100">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-base" title={note.category}>
                        {categoryIcon[note.category] ?? '📝'}
                    </span>
                    <span className="text-xs font-semibold text-gray-700 capitalize">{note.category}</span>
                    <span className="text-gray-300">·</span>
                    <div className="flex items-center gap-1 text-[11px] text-gray-400">
                        <User className="w-3 h-3" />
                        <span className="font-medium text-gray-600">{note.author}</span>
                    </div>
                    {note.contact_name && (
                        <>
                            <span className="text-gray-300">·</span>
                            <span className="text-[11px] text-gray-500 italic">{note.contact_name}</span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-1.5 ml-2 shrink-0">
                    {note.sentiment && (
                        <Badge
                            variant="outline"
                            className={`text-[10px] font-semibold capitalize ${sentimentColor[note.sentiment] ?? 'bg-gray-100 text-gray-500'}`}
                        >
                            {note.sentiment}
                        </Badge>
                    )}
                    {note.priority && (
                        <Badge
                            variant="outline"
                            className={`text-[10px] font-semibold capitalize ${priorityColor[note.priority] ?? 'bg-gray-100 text-gray-500'}`}
                        >
                            {note.priority}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="px-4 py-3 space-y-2">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{displayText}</p>
                {isLong && (
                    <button
                        onClick={() => setExpanded(v => !v)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-intellend-green hover:underline"
                    >
                        {expanded
                            ? <><ChevronUp className="w-3 h-3" /> Show less</>
                            : <><ChevronDown className="w-3 h-3" /> Show more</>}
                    </button>
                )}

                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 border-t border-gray-50">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDateTime(note.date)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-gray-500">
                        {statusIcon[note.status] ?? null}
                        <span className="capitalize font-medium">{note.status}</span>
                    </div>
                    {note.next_meeting && (
                        <div className="flex items-center gap-1 text-[10px] text-blue-500">
                            <Clock className="w-3 h-3" />
                            <span>
                                Next: {formatDate(note.next_meeting)}
                                {note.next_meeting_time ? ` ${note.next_meeting_time}` : ''}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

export const CRMLeadNotesModal: React.FC<CRMLeadNotesModalProps> = ({
    leadId,
    leadName,
    isOpen,
    onClose,
}) => {
    const { user } = useAuth();
    const [notes, setNotes] = useState<CRMLeadNote[]>([]);
    const [contacts, setContacts] = useState<ContactOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showAddDialog, setShowAddDialog] = useState(false);

    useEffect(() => {
        if (!isOpen || leadId == null) return;
        setNotes([]);
        setContacts([]);
        setError(null);
        setShowAddDialog(false);
        setLoading(true);

        // Fetch notes and lead detail (for contacts) in parallel
        Promise.all([
            axios.post(`/api/public/crm_lead_notes/`, { lead_id: leadId }),
            crmService.getLead(String(leadId)),
        ])
            .then(([notesRes, leadRes]) => {
                setNotes(notesRes.data as CRMLeadNote[]);

                // Build contacts list the same way CRMLeadForm does:
                // primary contact first, then additional_contacts
                const lead = leadRes.data as {
                    contact_name?: string;
                    additional_contacts?: LeadContact[];
                };
                const contactList: ContactOption[] = [];
                if (lead.contact_name) {
                    contactList.push({ id: 'primary', name: lead.contact_name, type: 'primary' });
                }
                if (Array.isArray(lead.additional_contacts)) {
                    lead.additional_contacts.forEach((c) => {
                        if (c.name) contactList.push({ id: c.id, name: c.name, type: c.type });
                    });
                }
                setContacts(contactList);
            })
            .catch(() => setError('Failed to load notes. Please try again.'))
            .finally(() => setLoading(false));
    }, [isOpen, leadId]);

    // Called by AddNoteDialog after a successful save — reload notes list
    const handleNoteSaved = () => {
        if (leadId == null) return;
        setLoading(true);
        axios
            .post(`/api/public/crm_lead_notes/`, { lead_id: leadId })
            .then(res => setNotes(res.data as CRMLeadNote[]))
            .catch(() => {/* silently ignore refresh errors */})
            .finally(() => setLoading(false));
    };

    const authorName = user?.username ?? 'Agent';

    return (
        <>
            <Dialog open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden">
                    {/* Header */}
                    <DialogHeader className="px-6 py-4 border-b bg-white shrink-0">
                        <DialogTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                            <StickyNote className="w-4 h-4 text-intellend-green" />
                            <span>CRM Notes</span>
                            {leadName && (
                                <span className="text-gray-400 font-normal text-sm">
                                    — <span className="text-gray-700 font-medium">{leadName}</span>
                                </span>
                            )}
                            {leadId != null && (
                                <span className="text-[11px] font-mono text-gray-400 ml-1">#{leadId}</span>
                            )}
                        </DialogTitle>
                        {!loading && !error && (
                            <p className="text-xs text-gray-400 mt-0.5">
                                {notes.length === 0
                                    ? 'No notes yet for this lead.'
                                    : `${notes.length} note${notes.length !== 1 ? 's' : ''}`}
                            </p>
                        )}
                    </DialogHeader>

                    {/* Body */}
                    <div className="overflow-y-auto flex-1 p-5 bg-gray-50/60 space-y-4">
                        {/* Add Note Button */}
                        {leadId != null && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowAddDialog(true)}
                                className="w-full h-9 text-xs font-semibold border-dashed border-intellend-green/40 text-intellend-green hover:bg-intellend-green/5 hover:border-intellend-green/60 gap-1.5"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add New Note
                            </Button>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <Loader2 className="w-8 h-8 text-intellend-green animate-spin" />
                                <p className="text-sm text-gray-400">Loading notes…</p>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="flex items-center justify-center py-16">
                                <p className="text-sm text-red-500">{error}</p>
                            </div>
                        )}

                        {/* No notes */}
                        {!loading && !error && notes.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                    <MessageSquare className="w-5 h-5 text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-500">No notes yet</p>
                                <p className="text-xs text-gray-400 max-w-xs">
                                    Add the first note for this CRM lead using the button above.
                                </p>
                            </div>
                        )}

                        {/* Notes list */}
                        {!loading && !error && notes.length > 0 && (
                            <div className="space-y-3">
                                {notes.map(note => (
                                    <NoteCard key={note.id} note={note} />
                                ))}
                            </div>
                        )}

                        {/* No lead linked */}
                        {leadId == null && !loading && (
                            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Tag className="w-5 h-5 text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-500">No CRM lead linked</p>
                                <p className="text-xs text-gray-400 max-w-xs">
                                    This user does not have a linked CRM lead, so notes cannot be displayed.
                                </p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Note Dialog — rendered outside the main Dialog to avoid nesting issues */}
            {leadId != null && (
                <AddNoteDialog
                    open={showAddDialog}
                    leadId={leadId}
                    leadLabel={leadName}
                    authorName={authorName}
                    contacts={contacts}
                    onClose={() => setShowAddDialog(false)}
                    onSaved={handleNoteSaved}
                />
            )}
        </>
    );
};

export default CRMLeadNotesModal;
